import React, { useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { hrefFor } from '../../routers';
import { PaymentSource } from '../../constants';
import { AuthPayload } from '../../lib/authenticate';
import { reportError } from '../../lib/reportError';
import { useUser } from '../../hooks/useUser';
import { useErrorModal } from '../../hooks/useErrorModal';
import { useSocialAuth, SocialAuthProvider } from '../../hooks/useSocialAuth';
import {
  useUpdateCartMutation,
  CartUpdateAction,
  CartUpdatePropertyName
} from '../../hooks/useCart';
import { useCountry } from '../CountrySelector';
import { PaymentSourceButton, PaymentSourceButtonProps } from './PaymentSourceButton';

type Props = Partial<PaymentSourceButtonProps>;

export const AmazonPayButton: React.FC<Props> = props => {
  const location = useLocation();
  const history = useHistory();
  const { showErrorModal } = useErrorModal();
  const {
    country: { currencyCode }
  } = useCountry();
  const {
    user: { isLoggedIn }
  } = useUser();
  const [updateCart, { loading }] = useUpdateCartMutation();

  const goToAmazonCheckout = useCallback(
    async (user?: AuthPayload) => {
      try {
        await updateCart(CartUpdateAction.PaymentSource, {
          id: user?.cartId?.toString(),
          [CartUpdatePropertyName.PaymentSource]: PaymentSource.Amazon
        });

        if (location.pathname !== hrefFor.CheckoutPage()) {
          history.push(hrefFor.CheckoutPage());
        }
      } catch (error) {
        reportError(error);
        showErrorModal();
      }
    },
    [updateCart, history, showErrorModal, location.pathname]
  );

  const { handleLogin, renderModals } = useSocialAuth(
    SocialAuthProvider.Amazon,
    goToAmazonCheckout
  );

  const handleClick = useCallback(() => (isLoggedIn ? goToAmazonCheckout() : handleLogin()), [
    isLoggedIn,
    goToAmazonCheckout,
    handleLogin
  ]);

  if (currencyCode !== 'USD') return null;

  return (
    <>
      <PaymentSourceButton
        {...props}
        label="Amazon Pay"
        image="/amazon_pay.png"
        loading={loading}
        onClick={handleClick}
      />
      {renderModals()}
    </>
  );
};
